/**
 * HTTP Client
 * Centralized fetch wrapper using Bearer JWT tokens.
 */

import { getPersistedAuthToken } from "../auth/authSession";
import { API_BASE_URL } from '../config/environment';

let onUnauthorized = null;

/** Register a callback that fires when an authenticated request receives a 401. */
export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler;
};

/**
 * Map an HTTP status code to a user-friendly error message.
 * Uses the backend's error message when available.
 */
const getHttpErrorMessage = (status, data) => {
  const backendMessage = data?.error?.message || data?.message;

  switch (status) {
    case 400:
      return backendMessage || 'Invalid request. Please check your input and try again.';
    case 401:
      return backendMessage || 'Incorrect email or password.';
    case 403:
      return backendMessage || 'You do not have permission to perform this action.';
    case 404:
      return backendMessage || 'The requested resource was not found.';
    case 409:
      return backendMessage || 'This record already exists.';
    case 422:
      return backendMessage || 'Validation failed. Please check your input.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
    case 502:
    case 503:
      return 'Server error. Please try again later or contact support.';
    default:
      return backendMessage || 'Something went wrong. Please try again.';
  }
};

/**
 * Core request function.
 * @param {string} endpoint 
 * @param {object} options
 * @param {string}  options.method   
 * @param {object}  options.body    
 * @param {boolean} options.skipAuth 
 */
export const apiClient = async (endpoint, options = {}) => {
  const { method = 'GET', body, skipAuth = false, headers = {} } = options;

  const token = getPersistedAuthToken();

  const requestHeaders = {
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    ...(!skipAuth && token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  const config = {
    method,
    headers: requestHeaders,
    ...(body ? { body: JSON.stringify(body) } : {}),
  };

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    let data;
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      if (response.status === 401 && !skipAuth) {
        onUnauthorized?.();
        return { error: { message: "Your session has expired. Please sign in again.", statusCode: 401 } };
      }
      const friendlyMessage = getHttpErrorMessage(response.status, data);
      return { error: { message: friendlyMessage, statusCode: response.status } };
    }

    return data;
  } catch (error) {
    // Network-level failure: offline, CORS, DNS, timeout
    const isNetworkError = error instanceof TypeError;
    const friendlyMessage = isNetworkError
      ? 'Unable to connect to the server. Please check your internet connection and try again.'
      : 'An unexpected error occurred. Please try again.';
    console.error(`[API Client] ${method} ${endpoint}:`, error.message);
    return { error: { message: friendlyMessage, statusCode: 0 } };
  }
};

export const get  = (endpoint, options = {}) => apiClient(endpoint, { ...options, method: 'GET' });
export const post = (endpoint, body, options = {}) => apiClient(endpoint, { ...options, method: 'POST', body });
export const put  = (endpoint, body, options = {}) => apiClient(endpoint, { ...options, method: 'PUT', body });
export const patch = (endpoint, body, options = {}) => apiClient(endpoint, { ...options, method: 'PATCH', body });
export const del  = (endpoint, options = {}) => apiClient(endpoint, { ...options, method: 'DELETE' });

export default apiClient;